// Support-ticket client (ai-agent-service /api/v1/ai/tickets). The structured
// "Report a payroll issue" form posts here — the backend reuses Dana's ticket
// flow (dedupe + HR escalation email + employee acknowledgement), so nothing
// becomes an untracked email. Same auth pattern as payApi.
import axios from 'axios';
import { apiConfig } from '@/config/api.config';
import { attachCookieAuth } from '@/lib/session';
import type { MyTicket } from '../types';

const http = axios.create({ baseURL: apiConfig.baseURL, timeout: 30000, withCredentials: true });
http.interceptors.request.use(attachCookieAuth);

export type IssueCategory =
  | 'missing_earning'
  | 'incorrect_deduction'
  | 'loan_deduction_question'
  | 'salary_advance_question'
  | 'incorrect_payment_account'
  | 'payment_not_received'
  | 'tax_question'
  | 'benefit_contribution_question'
  | 'other';

export const ISSUE_CATEGORIES: IssueCategory[] = [
  'missing_earning',
  'incorrect_deduction',
  'loan_deduction_question',
  'salary_advance_question',
  'incorrect_payment_account',
  'payment_not_received',
  'tax_question',
  'benefit_contribution_question',
  'other',
];

export interface IssueInput {
  category: IssueCategory;
  subject: string;
  description: string;
  period?: string;
  lineItem?: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  consent: boolean;
}

export interface TicketResult {
  ok: boolean;
  ticketRef: string;
  deduped: boolean;
  note?: string;
}

export async function createPayrollIssue(input: IssueInput): Promise<TicketResult> {
  const { data } = await http.post<any>('/api/v1/ai/tickets', {
    module: 'payroll',
    category: input.category,
    subject: input.subject,
    description: input.description,
    period: input.period,
    line_item: input.lineItem,
    priority: input.priority,
    consent: input.consent,
  });
  return {
    ok: !!data.ok,
    ticketRef: data.ticket_ref ?? '',
    deduped: !!data.deduped,
    note: data.note,
  };
}

export async function listMyTickets(): Promise<MyTicket[]> {
  const { data } = await http.get<{ tickets: any[] }>('/api/v1/ai/tickets/mine');
  return (data.tickets ?? []).map((t) => ({
    ref: t.ref ?? '',
    module: t.module ?? '',
    category: t.category ?? '',
    priority: t.priority ?? 'normal',
    status: t.status ?? '',
    subject: t.subject ?? '',
    createdAt: t.created_at ?? '',
  }));
}
